import { useGetCurrentUserErpCustomers } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import { useGetCurrentUserErpCustomersSelector } from 'gcs-common/clients/api/entities/current-user/current-user-api-selectors';
import { useState } from 'react';
import Select, { type StylesConfig } from 'react-select';
import { useDebouncedCallback } from 'use-debounce';

interface OptionType {
  value: string;
  label: string;
}

interface ErpCustomerDropDownProps {
  onChange: (newValue: string | string[] | undefined) => void;
  isClearable?: boolean;
  isMulti?: boolean;
  placeholder?: string;
  value: string[] | string | undefined;
}

const ErpCustomerDropDown = (props: ErpCustomerDropDownProps) => {
  const { onChange, isClearable = false, isMulti = false, placeholder = '', value } = props;

  const [inputValue, setInputValue] = useState('');
  // This one should be added to the url params for the erp customers
  const [searchText, setSearchText] = useState<string | undefined>();
  const { data: customers, isFetching } = useGetCurrentUserErpCustomers({
    query: {
      search: searchText,
      plain_select: true,
      size: 30,
    },
  });

  // A list of all previously fetched customers
  // Used for persisting the selected items if they are not in the current search query
  const allCustomersList = useGetCurrentUserErpCustomersSelector();
  const allCustomersListOptions: OptionType[] =
    allCustomersList.map((el) => ({
      label: `${el.friendlyName} (${el.customerNumber})`,
      value: el.id,
    })) ?? [];

  const handleChange = (selectedOption: OptionType | OptionType[] | undefined) => {
    let values: string | string[] | undefined;
    if (Array.isArray(selectedOption)) {
      values = selectedOption.map((x) => x.value);
    } else {
      values = selectedOption?.value || undefined;
    }

    onChange(values);
  };

  const [debouncedSearch] = useDebouncedCallback((value: string) => {
    setSearchText(value);
  }, 500);

  const onSearchTextChange = (searchText: string) => {
    debouncedSearch(searchText);
    setInputValue(searchText);
    return searchText;
  };

  const customersOptions: OptionType[] =
    customers?.data?.map((el) => ({
      label: `${el.friendlyName} (${el.customerNumber})`,
      value: el.id,
    })) ?? [];

  const customStyles: StylesConfig<OptionType> = {
    container: (provided) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: '8px',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
    }),
    valueContainer: (provided, _state) => ({
      ...provided,
      maxHeight: '70px',
      overflowY: 'auto',
    }),
  };

  const defaultValue = !value
    ? undefined
    : isMulti
      ? allCustomersListOptions.filter((x) => value.includes(x.value))
      : allCustomersListOptions.find((x) => x.value === value);

  return (
    <Select<OptionType>
      /** Force re-render when options are loaded to update defaultValue. */
      name="erpCustomerId"
      value={defaultValue}
      options={customersOptions}
      isLoading={isFetching}
      isClearable={isClearable}
      onChange={(selectedOption) => handleChange(selectedOption || undefined)}
      styles={customStyles}
      placeholder={placeholder}
      // @ts-expect-error idk ¯\_(ツ)_/¯ Error: Type 'boolean' is not assignable to type 'false'
      isMulti={isMulti}
      onInputChange={onSearchTextChange}
      inputValue={inputValue}
      noOptionsMessage={() => 'Keine Kunden gefunden'}
    />
  );
};

export default ErpCustomerDropDown;
